import { FC, useRef, useState } from 'react'
import moment from 'moment'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar-line-16.svg'
import { ReactComponent as ChevronDownIcon } from 'src/assets/svg/chevron-down-16.svg'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { cn } from 'src/utils/cn'
import styles from './periodPicker.module.scss'

interface PeriodPickerProps {
  // Shown dimmed and unopenable — the periods of a generated report are fixed.
  disabled?: boolean
  onApply: (start: moment.Moment, end: moment.Moment) => void
  placeholder: string
  value?: string
}

type QuickRange = {
  end: () => moment.Moment
  label: string
  start: () => moment.Moment
}

// "Custom Range" is the default selection: it is the state the calendar is
// already in when the panel opens, before any shortcut narrows it.
const CUSTOM_RANGE = 'Custom Range'

const QUICK_RANGES: QuickRange[] = [
  { end: () => moment(), label: 'Today', start: () => moment() },
  {
    end: () => moment().subtract(1, 'day'),
    label: 'Yesterday',
    start: () => moment().subtract(1, 'day'),
  },
  {
    end: () => moment(),
    label: 'Last 7 days',
    start: () => moment().subtract(6, 'days'),
  },
  {
    end: () => moment(),
    label: 'Last 30 days',
    start: () => moment().subtract(29, 'days'),
  },
  {
    end: () => moment().endOf('month'),
    label: 'This Month',
    start: () => moment().startOf('month'),
  },
  {
    end: () => moment().subtract(1, 'month').endOf('month'),
    label: 'Last Month',
    start: () => moment().subtract(1, 'month').startOf('month'),
  },
  {
    end: () => moment().endOf('quarter'),
    label: 'This Quarter',
    start: () => moment().startOf('quarter'),
  },
  {
    end: () => moment().subtract(1, 'quarter').endOf('quarter'),
    label: 'Last Quarter',
    start: () => moment().subtract(1, 'quarter').startOf('quarter'),
  },
]

export const PeriodPicker: FC<PeriodPickerProps> = ({
  disabled = false,
  onApply,
  placeholder,
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeQuickRange, setActiveQuickRange] = useState(CUSTOM_RANGE)
  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    onOutsideClick: () => setIsOpen(false),
    ref: containerRef,
  })

  const handleQuickRange = (range: QuickRange) => {
    setActiveQuickRange(range.label)
    onApply(range.start(), range.end())
    setIsOpen(false)
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={cn(
          styles.trigger,
          isOpen && styles.triggerOpen,
          disabled && styles.triggerDisabled
        )}
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <span className={styles.triggerValue}>
          <CalendarIcon className={styles.triggerIcon} />
          {value ?? placeholder}
        </span>
        <ChevronDownIcon
          className={cn(styles.triggerIcon, isOpen && styles.triggerIconOpen)}
        />
      </button>

      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.quickList}>
            {QUICK_RANGES.map((range) => (
              <button
                className={cn(
                  styles.quickOption,
                  activeQuickRange === range.label && styles.quickOptionActive
                )}
                key={range.label}
                onClick={() => handleQuickRange(range)}
                type="button"
              >
                {range.label}
              </button>
            ))}
            <button
              className={cn(
                styles.quickOption,
                activeQuickRange === CUSTOM_RANGE && styles.quickOptionActive
              )}
              onClick={() => setActiveQuickRange(CUSTOM_RANGE)}
              type="button"
            >
              {CUSTOM_RANGE}
            </button>
          </div>

          <div className={styles.calendarPane}>
            <CustomCalendar
              className={styles.calendar}
              monthsToShow={2}
              navLabel="Navigation for the selected period"
              onApply={(start, end) => {
                setActiveQuickRange(CUSTOM_RANGE)
                onApply(start, end)
              }}
              onClose={() => setIsOpen(false)}
              showRangeLabel
              // Dots under the days that actually hold mentions, so a period is
              // picked knowing where the data is rather than by trial and error.
              source="social-listening"
            />
          </div>
        </div>
      )}
    </div>
  )
}
